// Copyright text placeholder, Warner Bros. Discovery, Inc.

/**
 * The "CLDR Locale" codes used to identify sets of formatting rules in the Unicode CLDR data set
 * (see https://wiki.hbo.com/pages/viewpage.action?pageId=160140159#LanguageCodesandFormatCodes:Locale\>GloablizationContext-UnicodeCLDRLocaleCodes)
 *
 * This code is generated based on the current Unicode CLDR data set as determined by the 'hbo-cldr-data-subset' dependency in
 * `localization-enum-generator/package.json`.
 *
 * The IETF BCP 47 language tag standard permits certain subtags to be omitted, in cases where doing so results in an
 * unambiguous language tag. For example, `en-Latn-US` can correctly be written as `en-US`, since there are no other
 * scripts in which US English is written. By contrast, `sr-Cyrl-RS` cannot correctly be written as `sr-RS`, because
 * both Cyrillic and Latin scripts are used to write Serbian in Serbia.
 *
 * The CLDR dataset does not respect these subtag-omission rules. Instead, it frequently represents "CLDR Locales" with
 * incorrectly shortened language tags such as `pt` and `es`. It does this based on its own heuristic, changeable
 * estimation of which subtag values are "most prevalent". For example, `pt` actually means `pt-BR`, because there
 * are more speakers of Portuguese in Brazil than in Portugal. However, this rule is not consistently applied: `es`
 * means `es-ES`, even though there are more speakers of Spanish in Mexico than in Spain.
 *
 * The CLDR dataset does provide a mapping between each abbreviated Unicode CLDR Locale language tag and the corresponding,
 * fully expanded, three-subtag (language+script+region) language tag, which it calls the "max language ID". For example, the
 * "max language ID" of `es` is `es-Latn-ES`, and the "max language ID" of `pt` is `pt-Latn-BR`.
 *
 * When mapping Unicode CLDR Locale ('CldrLocale') language tags to HBO standard ('CanonicalizedCldrLocale') language tags, we
 * make use of the CLDR dataset's "max language ID" mapping to expand the Unicode CLDR Locale values into unambiguous form.
 *
 * This file was generated by `localization-enum-generator`. Please edit the template there to make any desired
 * changes. Changes made directly to this file will be overwritten without warning if this file is re-generated.
 *
 * @public
 */
export const enum CldrLocale {
  /** Afrikaans in South Africa in Latin script. Equivalent HBO CLDR Locale: "af-ZA". */
  AF = 'af',

  /** Afrikaans in Namibia in Latin script. */
  AF_NA = 'af-NA',

  /** Aghem in Cameroon in Latin script. Equivalent HBO CLDR Locale: "agq-CM". */
  AGQ = 'agq',

  /** Akan in Ghana in Latin script. Equivalent HBO CLDR Locale: "ak-GH". */
  AK = 'ak',

  /** Amharic in Ethiopia in Ethiopic | Geʻez | Ge'ez script. Equivalent HBO CLDR Locale: "am-ET". */
  AM = 'am',

  /** Arabic in United Arab Emirates in Arabic script. */
  AR_AE = 'ar-AE',

  /** Arabic in Bahrain in Arabic script. */
  AR_BH = 'ar-BH',

  /** Arabic in Djibouti in Arabic script. */
  AR_DJ = 'ar-DJ',

  /** Arabic in Algeria in Arabic script. */
  AR_DZ = 'ar-DZ',

  /** Arabic in Egypt in Arabic script. */
  AR_EG = 'ar-EG',

  /** Arabic in Western Sahara in Arabic script. */
  AR_EH = 'ar-EH',

  /** Arabic in Eritrea in Arabic script. */
  AR_ER = 'ar-ER',

  /** Arabic in Israel in Arabic script. */
  AR_IL = 'ar-IL',

  /** Arabic in Iraq in Arabic script. */
  AR_IQ = 'ar-IQ',

  /** Arabic in Jordan in Arabic script. */
  AR_JO = 'ar-JO',

  /** Arabic in Comoros in Arabic script. */
  AR_KM = 'ar-KM',

  /** Arabic in Kuwait in Arabic script. */
  AR_KW = 'ar-KW',

  /** Arabic in Lebanon in Arabic script. */
  AR_LB = 'ar-LB',

  /** Arabic in Libya in Arabic script. */
  AR_LY = 'ar-LY',

  /** Arabic in Morocco in Arabic script. */
  AR_MA = 'ar-MA',

  /** Arabic in Mauritania in Arabic script. */
  AR_MR = 'ar-MR',

  /** Arabic in Oman in Arabic script. */
  AR_OM = 'ar-OM',

  /** Arabic in State of Palestine in Arabic script. */
  AR_PS = 'ar-PS',

  /** Arabic in Qatar in Arabic script. */
  AR_QA = 'ar-QA',

  /** Arabic in Saudi Arabia in Arabic script. */
  AR_SA = 'ar-SA',

  /** Arabic in Sudan in Arabic script. */
  AR_SD = 'ar-SD',

  /** Arabic in Somalia in Arabic script. */
  AR_SO = 'ar-SO',

  /** Arabic in South Sudan in Arabic script. */
  AR_SS = 'ar-SS',

  /** Arabic in Syrian Arab Republic in Arabic script. */
  AR_SY = 'ar-SY',

  /** Arabic in Chad in Arabic script. */
  AR_TD = 'ar-TD',

  /** Arabic in Tunisia in Arabic script. */
  AR_TN = 'ar-TN',

  /** Arabic in Yemen in Arabic script. */
  AR_YE = 'ar-YE',

  /** Assamese in India in Bengali | Bangla script. Equivalent HBO CLDR Locale: "as-IN". */
  AS = 'as',

  /** Asu (Tanzania) in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "asa-TZ". */
  ASA = 'asa',

  /** Asturian | Asturleonese | Bable | Leonese in Spain in Latin script. Equivalent HBO CLDR Locale: "ast-ES". */
  AST = 'ast',

  /** Azerbaijani in Azerbaijan in Latin script. Equivalent HBO CLDR Locale: "az-AZ". */
  AZ_LATN = 'az-Latn',

  /** Basa (Cameroon) in Cameroon in Latin script. Equivalent HBO CLDR Locale: "bas-CM". */
  BAS = 'bas',

  /** Belarusian in Belarus in Cyrillic script. Equivalent HBO CLDR Locale: "be-BY". */
  BE = 'be',

  /** Bemba (Zambia) in Zambia in Latin script. Equivalent HBO CLDR Locale: "bem-ZM". */
  BEM = 'bem',

  /** Bena (Tanzania) in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "bez-TZ". */
  BEZ = 'bez',

  /** Bulgarian in Bulgaria in Cyrillic script. Equivalent HBO CLDR Locale: "bg-BG". */
  BG = 'bg',

  /** Bambara in Mali in Latin script. Equivalent HBO CLDR Locale: "bm-ML". */
  BM = 'bm',

  /** Bengali in Bangladesh in Bengali | Bangla script. Equivalent HBO CLDR Locale: "bn-BD". */
  BN = 'bn',

  /** Bengali in India in Bengali | Bangla script. */
  BN_IN = 'bn-IN',

  /** Tibetan in China in Tibetan script. Equivalent HBO CLDR Locale: "bo-CN". */
  BO = 'bo',

  /** Tibetan in India in Tibetan script. */
  BO_IN = 'bo-IN',

  /** Breton in France in Latin script. Equivalent HBO CLDR Locale: "br-FR". */
  BR = 'br',

  /** Bodo (India) in India in Devanagari | Nagari script. Equivalent HBO CLDR Locale: "brx-IN". */
  BRX = 'brx',

  /** Bosnian in Bosnia and Herzegovina in Latin script. Equivalent HBO CLDR Locale: "bs-BA". */
  BS_LATN = 'bs-Latn',

  /** Catalan |  Valencian in Spain in Latin script. Equivalent HBO CLDR Locale: "ca-ES". */
  CA = 'ca',

  /** Catalan |  Valencian in Andorra in Latin script. */
  CA_AD = 'ca-AD',

  /** Catalan |  Valencian in France in Latin script. */
  CA_FR = 'ca-FR',

  /** Catalan |  Valencian in Italy in Latin script. */
  CA_IT = 'ca-IT',

  /** Chakma in Bangladesh in Chakma script. Equivalent HBO CLDR Locale: "ccp-BD". */
  CCP = 'ccp',

  /** Chakma in India in Chakma script. */
  CCP_IN = 'ccp-IN',

  /** Chechen in Russian Federation in Cyrillic script. Equivalent HBO CLDR Locale: "ce-RU". */
  CE = 'ce',

  /** Cebuano in Philippines in Latin script. Equivalent HBO CLDR Locale: "ceb-PH". */
  CEB = 'ceb',

  /** Chiga in Uganda in Latin script. Equivalent HBO CLDR Locale: "cgg-UG". */
  CGG = 'cgg',

  /** Cherokee in United States in Cherokee script. Equivalent HBO CLDR Locale: "chr-US". */
  CHR = 'chr',

  /** Central Kurdish in Iraq in Arabic script. Equivalent HBO CLDR Locale: "ckb-IQ". */
  CKB = 'ckb',

  /** Central Kurdish in Islamic Republic of Iran in Arabic script. */
  CKB_IR = 'ckb-IR',

  /** Czech in Czechia | Czech Republic in Latin script. Equivalent HBO CLDR Locale: "cs-CZ". */
  CS = 'cs',

  /** Church Slavic |  Old Slavonic |  Church Slavonic |  Old Bulgarian |  Old Church Slavonic in Russian Federation in Cyrillic script. Equivalent HBO CLDR Locale: "cu-RU". */
  CU = 'cu',

  /** Welsh in United Kingdom in Latin script. Equivalent HBO CLDR Locale: "cy-GB". */
  CY = 'cy',

  /** Danish in Denmark in Latin script. Equivalent HBO CLDR Locale: "da-DK". */
  DA = 'da',

  /** Danish in Greenland in Latin script. */
  DA_GL = 'da-GL',

  /** Taita | Dawida in Kenya in Latin script. Equivalent HBO CLDR Locale: "dav-KE". */
  DAV = 'dav',

  /** German in Germany in Latin script. Equivalent HBO CLDR Locale: "de-DE". */
  DE = 'de',

  /** German in Austria in Latin script. */
  DE_AT = 'de-AT',

  /** German in Belgium in Latin script. */
  DE_BE = 'de-BE',

  /** German in Switzerland in Latin script. */
  DE_CH = 'de-CH',

  /** German in Italy in Latin script. */
  DE_IT = 'de-IT',

  /** German in Liechtenstein in Latin script. */
  DE_LI = 'de-LI',

  /** German in Luxembourg in Latin script. */
  DE_LU = 'de-LU',

  /** Zarma in Niger in Latin script. Equivalent HBO CLDR Locale: "dje-NE". */
  DJE = 'dje',

  /** Lower Sorbian in Germany in Latin script. Equivalent HBO CLDR Locale: "dsb-DE". */
  DSB = 'dsb',

  /** Duala in Cameroon in Latin script. Equivalent HBO CLDR Locale: "dua-CM". */
  DUA = 'dua',

  /** Jola-Fonyi in Senegal in Latin script. Equivalent HBO CLDR Locale: "dyo-SN". */
  DYO = 'dyo',

  /** Dzongkha in Bhutan in Tibetan script. Equivalent HBO CLDR Locale: "dz-BT". */
  DZ = 'dz',

  /** Embu | Kiembu in Kenya in Latin script. Equivalent HBO CLDR Locale: "ebu-KE". */
  EBU = 'ebu',

  /** Ewe in Ghana in Latin script. Equivalent HBO CLDR Locale: "ee-GH". */
  EE = 'ee',

  /** Ewe in Togo in Latin script. */
  EE_TG = 'ee-TG',

  /** Greek, Modern (1453-) in Greece in Greek script. Equivalent HBO CLDR Locale: "el-GR". */
  EL = 'el',

  /** Greek, Modern (1453-) in Cyprus in Greek script. */
  EL_CY = 'el-CY',

  /** English in United States in Latin script. Equivalent HBO CLDR Locale: "en-US". */
  EN = 'en',

  /**
   * English in World in Latin script.
   *
   * WARNING: This is IN USE for normal-length pseudo-localization! DO NOT USE it for another purpose without addressing this issue!
   */
  EN_001 = 'en-001',

  /**
   * English in Europe in Latin script.
   *
   * WARNING: This is IN USE for extended-length pseudo-localization! DO NOT USE it for another purpose without addressing this issue!
   */
  EN_150 = 'en-150',

  /** English in United Arab Emirates in Latin script. */
  EN_AE = 'en-AE',

  /** English in Antigua and Barbuda in Latin script. */
  EN_AG = 'en-AG',

  /** English in Anguilla in Latin script. */
  EN_AI = 'en-AI',

  /** English in American Samoa in Latin script. */
  EN_AS = 'en-AS',

  /** English in Austria in Latin script. */
  EN_AT = 'en-AT',

  /** English in Australia in Latin script. */
  EN_AU = 'en-AU',

  /** English in Barbados in Latin script. */
  EN_BB = 'en-BB',

  /** English in Belgium in Latin script. */
  EN_BE = 'en-BE',

  /** English in Burundi in Latin script. */
  EN_BI = 'en-BI',

  /** English in Bermuda in Latin script. */
  EN_BM = 'en-BM',

  /** English in Bahamas in Latin script. */
  EN_BS = 'en-BS',

  /** English in Botswana in Latin script. */
  EN_BW = 'en-BW',

  /** English in Belize in Latin script. */
  EN_BZ = 'en-BZ',

  /** English in Canada in Latin script. */
  EN_CA = 'en-CA',

  /** English in Cocos (Keeling) Islands in Latin script. */
  EN_CC = 'en-CC',

  /** English in Switzerland in Latin script. */
  EN_CH = 'en-CH',

  /** English in Cook Islands in Latin script. */
  EN_CK = 'en-CK',

  /** English in Cameroon in Latin script. */
  EN_CM = 'en-CM',

  /** English in Christmas Island in Latin script. */
  EN_CX = 'en-CX',

  /** English in Cyprus in Latin script. */
  EN_CY = 'en-CY',

  /** English in Germany in Latin script. */
  EN_DE = 'en-DE',

  /** English in Diego Garcia in Latin script. */
  EN_DG = 'en-DG',

  /** English in Denmark in Latin script. */
  EN_DK = 'en-DK',

  /** English in Dominica in Latin script. */
  EN_DM = 'en-DM',

  /** English in Eritrea in Latin script. */
  EN_ER = 'en-ER',

  /** English in Finland in Latin script. */
  EN_FI = 'en-FI',

  /** English in Fiji in Latin script. */
  EN_FJ = 'en-FJ',

  /** English in Falkland Islands (Malvinas) in Latin script. */
  EN_FK = 'en-FK',

  /** English in Federated States of Micronesia in Latin script. */
  EN_FM = 'en-FM',

  /** English in United Kingdom in Latin script. */
  EN_GB = 'en-GB',

  /** English in Grenada in Latin script. */
  EN_GD = 'en-GD',

  /** English in Guernsey in Latin script. */
  EN_GG = 'en-GG',

  /** English in Ghana in Latin script. */
  EN_GH = 'en-GH',

  /** English in Gibraltar in Latin script. */
  EN_GI = 'en-GI',

  /** English in Gambia in Latin script. */
  EN_GM = 'en-GM',

  /** English in Guam in Latin script. */
  EN_GU = 'en-GU',

  /** English in Guyana in Latin script. */
  EN_GY = 'en-GY',

  /** English in Hong Kong in Latin script. */
  EN_HK = 'en-HK',

  /** English in Ireland in Latin script. */
  EN_IE = 'en-IE',

  /** English in Israel in Latin script. */
  EN_IL = 'en-IL',

  /** English in Isle of Man in Latin script. */
  EN_IM = 'en-IM',

  /** English in India in Latin script. */
  EN_IN = 'en-IN',

  /** English in British Indian Ocean Territory in Latin script. */
  EN_IO = 'en-IO',

  /** English in Jersey in Latin script. */
  EN_JE = 'en-JE',

  /** English in Jamaica in Latin script. */
  EN_JM = 'en-JM',

  /** English in Kenya in Latin script. */
  EN_KE = 'en-KE',

  /** English in Kiribati in Latin script. */
  EN_KI = 'en-KI',

  /** English in Saint Kitts and Nevis in Latin script. */
  EN_KN = 'en-KN',

  /** English in Cayman Islands in Latin script. */
  EN_KY = 'en-KY',

  /** English in Saint Lucia in Latin script. */
  EN_LC = 'en-LC',

  /** English in Liberia in Latin script. */
  EN_LR = 'en-LR',

  /** English in Lesotho in Latin script. */
  EN_LS = 'en-LS',

  /** English in Madagascar in Latin script. */
  EN_MG = 'en-MG',

  /** English in Marshall Islands in Latin script. */
  EN_MH = 'en-MH',

  /** English in Macao in Latin script. */
  EN_MO = 'en-MO',

  /** English in Northern Mariana Islands in Latin script. */
  EN_MP = 'en-MP',

  /** English in Montserrat in Latin script. */
  EN_MS = 'en-MS',

  /** English in Malta in Latin script. */
  EN_MT = 'en-MT',

  /** English in Mauritius in Latin script. */
  EN_MU = 'en-MU',

  /** English in Malawi in Latin script. */
  EN_MW = 'en-MW',

  /** English in Malaysia in Latin script. */
  EN_MY = 'en-MY',

  /** English in Namibia in Latin script. */
  EN_NA = 'en-NA',

  /** English in Norfolk Island in Latin script. */
  EN_NF = 'en-NF',

  /** English in Nigeria in Latin script. */
  EN_NG = 'en-NG',

  /** English in Netherlands in Latin script. */
  EN_NL = 'en-NL',

  /** English in Nauru in Latin script. */
  EN_NR = 'en-NR',

  /** English in Niue in Latin script. */
  EN_NU = 'en-NU',

  /** English in New Zealand in Latin script. */
  EN_NZ = 'en-NZ',

  /** English in Papua New Guinea in Latin script. */
  EN_PG = 'en-PG',

  /** English in Philippines in Latin script. */
  EN_PH = 'en-PH',

  /** English in Pakistan in Latin script. */
  EN_PK = 'en-PK',

  /** English in Pitcairn in Latin script. */
  EN_PN = 'en-PN',

  /** English in Puerto Rico in Latin script. */
  EN_PR = 'en-PR',

  /** English in Palau in Latin script. */
  EN_PW = 'en-PW',

  /** English in Rwanda in Latin script. */
  EN_RW = 'en-RW',

  /** English in Solomon Islands in Latin script. */
  EN_SB = 'en-SB',

  /** English in Seychelles in Latin script. */
  EN_SC = 'en-SC',

  /** English in Sudan in Latin script. */
  EN_SD = 'en-SD',

  /** English in Sweden in Latin script. */
  EN_SE = 'en-SE',

  /** English in Singapore in Latin script. */
  EN_SG = 'en-SG',

  /** English in Saint Helena, Ascension and Tristan da Cunha in Latin script. */
  EN_SH = 'en-SH',

  /** English in Slovenia in Latin script. */
  EN_SI = 'en-SI',

  /** English in Sierra Leone in Latin script. */
  EN_SL = 'en-SL',

  /** English in South Sudan in Latin script. */
  EN_SS = 'en-SS',

  /** English in Sint Maarten (Dutch part) in Latin script. */
  EN_SX = 'en-SX',

  /** English in Eswatini | eSwatini | Swaziland in Latin script. */
  EN_SZ = 'en-SZ',

  /** English in Turks and Caicos Islands in Latin script. */
  EN_TC = 'en-TC',

  /** English in Tokelau in Latin script. */
  EN_TK = 'en-TK',

  /** English in Tonga in Latin script. */
  EN_TO = 'en-TO',

  /** English in Trinidad and Tobago in Latin script. */
  EN_TT = 'en-TT',

  /** English in Tuvalu in Latin script. */
  EN_TV = 'en-TV',

  /** English in United Republic of Tanzania in Latin script. */
  EN_TZ = 'en-TZ',

  /** English in Uganda in Latin script. */
  EN_UG = 'en-UG',

  /** English in United States Minor Outlying Islands in Latin script. */
  EN_UM = 'en-UM',

  /** English in Saint Vincent and the Grenadines in Latin script. */
  EN_VC = 'en-VC',

  /** English in British Virgin Islands in Latin script. */
  EN_VG = 'en-VG',

  /** English in U.S. Virgin Islands in Latin script. */
  EN_VI = 'en-VI',

  /** English in Vanuatu in Latin script. */
  EN_VU = 'en-VU',

  /** English in Samoa in Latin script. */
  EN_WS = 'en-WS',

  /** English in South Africa in Latin script. */
  EN_ZA = 'en-ZA',

  /** English in Zambia in Latin script. */
  EN_ZM = 'en-ZM',

  /** English in Zimbabwe in Latin script. */
  EN_ZW = 'en-ZW',

  /** Esperanto in World in Latin script. Equivalent HBO CLDR Locale: "eo-001". */
  EO = 'eo',

  /** Spanish |  Castilian in Spain in Latin script. Equivalent HBO CLDR Locale: "es-ES". */
  ES = 'es',

  /** Spanish |  Castilian in Latin America and the Caribbean in Latin script. */
  ES_419 = 'es-419',

  /** Spanish |  Castilian in Argentina in Latin script. */
  ES_AR = 'es-AR',

  /** Spanish |  Castilian in Bolivia in Latin script. */
  ES_BO = 'es-BO',

  /** Spanish |  Castilian in Brazil in Latin script. */
  ES_BR = 'es-BR',

  /** Spanish |  Castilian in Belize in Latin script. */
  ES_BZ = 'es-BZ',

  /** Spanish |  Castilian in Chile in Latin script. */
  ES_CL = 'es-CL',

  /** Spanish |  Castilian in Colombia in Latin script. */
  ES_CO = 'es-CO',

  /** Spanish |  Castilian in Costa Rica in Latin script. */
  ES_CR = 'es-CR',

  /** Spanish |  Castilian in Cuba in Latin script. */
  ES_CU = 'es-CU',

  /** Spanish |  Castilian in Dominican Republic in Latin script. */
  ES_DO = 'es-DO',

  /** Spanish |  Castilian in Ceuta, Melilla in Latin script. */
  ES_EA = 'es-EA',

  /** Spanish |  Castilian in Ecuador in Latin script. */
  ES_EC = 'es-EC',

  /** Spanish |  Castilian in Equatorial Guinea in Latin script. */
  ES_GQ = 'es-GQ',

  /** Spanish |  Castilian in Guatemala in Latin script. */
  ES_GT = 'es-GT',

  /** Spanish |  Castilian in Honduras in Latin script. */
  ES_HN = 'es-HN',

  /** Spanish |  Castilian in Canary Islands in Latin script. */
  ES_IC = 'es-IC',

  /** Spanish |  Castilian in Mexico in Latin script. */
  ES_MX = 'es-MX',

  /** Spanish |  Castilian in Nicaragua in Latin script. */
  ES_NI = 'es-NI',

  /** Spanish |  Castilian in Panama in Latin script. */
  ES_PA = 'es-PA',

  /** Spanish |  Castilian in Peru in Latin script. */
  ES_PE = 'es-PE',

  /** Spanish |  Castilian in Philippines in Latin script. */
  ES_PH = 'es-PH',

  /** Spanish |  Castilian in Puerto Rico in Latin script. */
  ES_PR = 'es-PR',

  /** Spanish |  Castilian in Paraguay in Latin script. */
  ES_PY = 'es-PY',

  /** Spanish |  Castilian in El Salvador in Latin script. */
  ES_SV = 'es-SV',

  /** Spanish |  Castilian in United States in Latin script. */
  ES_US = 'es-US',

  /** Spanish |  Castilian in Uruguay in Latin script. */
  ES_UY = 'es-UY',

  /** Spanish |  Castilian in Venezuela in Latin script. */
  ES_VE = 'es-VE',

  /** Estonian in Estonia in Latin script. Equivalent HBO CLDR Locale: "et-EE". */
  ET = 'et',

  /** Basque in Spain in Latin script. Equivalent HBO CLDR Locale: "eu-ES". */
  EU = 'eu',

  /** Ewondo in Cameroon in Latin script. Equivalent HBO CLDR Locale: "ewo-CM". */
  EWO = 'ewo',

  /** Persian in Islamic Republic of Iran in Arabic script. Equivalent HBO CLDR Locale: "fa-IR". */
  FA = 'fa',

  /** Persian in Afghanistan in Arabic script. */
  FA_AF = 'fa-AF',

  /** Fulah in Senegal in Latin script. Equivalent HBO CLDR Locale: "ff-SN". */
  FF_LATN = 'ff-Latn',

  /** Fulah in Burkina Faso in Latin script. */
  FF_LATN_BF = 'ff-Latn-BF',

  /** Fulah in Cameroon in Latin script. */
  FF_LATN_CM = 'ff-Latn-CM',

  /** Fulah in Ghana in Latin script. */
  FF_LATN_GH = 'ff-Latn-GH',

  /** Fulah in Gambia in Latin script. */
  FF_LATN_GM = 'ff-Latn-GM',

  /** Fulah in Guinea in Latin script. */
  FF_LATN_GN = 'ff-Latn-GN',

  /** Fulah in Guinea-Bissau in Latin script. */
  FF_LATN_GW = 'ff-Latn-GW',

  /** Fulah in Liberia in Latin script. */
  FF_LATN_LR = 'ff-Latn-LR',

  /** Fulah in Mauritania in Latin script. */
  FF_LATN_MR = 'ff-Latn-MR',

  /** Fulah in Niger in Latin script. */
  FF_LATN_NE = 'ff-Latn-NE',

  /** Fulah in Nigeria in Latin script. */
  FF_LATN_NG = 'ff-Latn-NG',

  /** Fulah in Sierra Leone in Latin script. */
  FF_LATN_SL = 'ff-Latn-SL',

  /** Finnish in Finland in Latin script. Equivalent HBO CLDR Locale: "fi-FI". */
  FI = 'fi',

  /** Filipino | Pilipino in Philippines in Latin script. Equivalent HBO CLDR Locale: "fil-PH". */
  FIL = 'fil',

  /** Faroese in Faroe Islands in Latin script. Equivalent HBO CLDR Locale: "fo-FO". */
  FO = 'fo',

  /** Faroese in Denmark in Latin script. */
  FO_DK = 'fo-DK',

  /** French in France in Latin script. Equivalent HBO CLDR Locale: "fr-FR". */
  FR = 'fr',

  /** French in Belgium in Latin script. */
  FR_BE = 'fr-BE',

  /** French in Burkina Faso in Latin script. */
  FR_BF = 'fr-BF',

  /** French in Burundi in Latin script. */
  FR_BI = 'fr-BI',

  /** French in Benin in Latin script. */
  FR_BJ = 'fr-BJ',

  /** French in Saint Barthélemy in Latin script. */
  FR_BL = 'fr-BL',

  /** French in Canada in Latin script. */
  FR_CA = 'fr-CA',

  /** French in The Democratic Republic of the Congo in Latin script. */
  FR_CD = 'fr-CD',

  /** French in Central African Republic in Latin script. */
  FR_CF = 'fr-CF',

  /** French in Congo in Latin script. */
  FR_CG = 'fr-CG',

  /** French in Switzerland in Latin script. */
  FR_CH = 'fr-CH',

  /** French in Côte d'Ivoire in Latin script. */
  FR_CI = 'fr-CI',

  /** French in Cameroon in Latin script. */
  FR_CM = 'fr-CM',

  /** French in Djibouti in Latin script. */
  FR_DJ = 'fr-DJ',

  /** French in Algeria in Latin script. */
  FR_DZ = 'fr-DZ',

  /** French in Gabon in Latin script. */
  FR_GA = 'fr-GA',

  /** French in French Guiana in Latin script. */
  FR_GF = 'fr-GF',

  /** French in Guinea in Latin script. */
  FR_GN = 'fr-GN',

  /** French in Guadeloupe in Latin script. */
  FR_GP = 'fr-GP',

  /** French in Equatorial Guinea in Latin script. */
  FR_GQ = 'fr-GQ',

  /** French in Haiti in Latin script. */
  FR_HT = 'fr-HT',

  /** French in Comoros in Latin script. */
  FR_KM = 'fr-KM',

  /** French in Luxembourg in Latin script. */
  FR_LU = 'fr-LU',

  /** French in Morocco in Latin script. */
  FR_MA = 'fr-MA',

  /** French in Monaco in Latin script. */
  FR_MC = 'fr-MC',

  /** French in Saint Martin (French part) in Latin script. */
  FR_MF = 'fr-MF',

  /** French in Madagascar in Latin script. */
  FR_MG = 'fr-MG',

  /** French in Mali in Latin script. */
  FR_ML = 'fr-ML',

  /** French in Martinique in Latin script. */
  FR_MQ = 'fr-MQ',

  /** French in Mauritania in Latin script. */
  FR_MR = 'fr-MR',

  /** French in Mauritius in Latin script. */
  FR_MU = 'fr-MU',

  /** French in New Caledonia in Latin script. */
  FR_NC = 'fr-NC',

  /** French in Niger in Latin script. */
  FR_NE = 'fr-NE',

  /** French in French Polynesia in Latin script. */
  FR_PF = 'fr-PF',

  /** French in Saint Pierre and Miquelon in Latin script. */
  FR_PM = 'fr-PM',

  /** French in Réunion in Latin script. */
  FR_RE = 'fr-RE',

  /** French in Rwanda in Latin script. */
  FR_RW = 'fr-RW',

  /** French in Seychelles in Latin script. */
  FR_SC = 'fr-SC',

  /** French in Senegal in Latin script. */
  FR_SN = 'fr-SN',

  /** French in Syrian Arab Republic in Latin script. */
  FR_SY = 'fr-SY',

  /** French in Chad in Latin script. */
  FR_TD = 'fr-TD',

  /** French in Togo in Latin script. */
  FR_TG = 'fr-TG',

  /** French in Tunisia in Latin script. */
  FR_TN = 'fr-TN',

  /** French in Vanuatu in Latin script. */
  FR_VU = 'fr-VU',

  /** French in Wallis and Futuna in Latin script. */
  FR_WF = 'fr-WF',

  /** French in Mayotte in Latin script. */
  FR_YT = 'fr-YT',

  /** Friulian in Italy in Latin script. Equivalent HBO CLDR Locale: "fur-IT". */
  FUR = 'fur',

  /** Western Frisian in Netherlands in Latin script. Equivalent HBO CLDR Locale: "fy-NL". */
  FY = 'fy',

  /** Irish in Ireland in Latin script. Equivalent HBO CLDR Locale: "ga-IE". */
  GA = 'ga',

  /** Irish in United Kingdom in Latin script. */
  GA_GB = 'ga-GB',

  /** Gaelic |  Scottish Gaelic in United Kingdom in Latin script. Equivalent HBO CLDR Locale: "gd-GB". */
  GD = 'gd',

  /** Galician in Spain in Latin script. Equivalent HBO CLDR Locale: "gl-ES". */
  GL = 'gl',

  /** Swiss German | Alemannic | Alsatian in Switzerland in Latin script. Equivalent HBO CLDR Locale: "gsw-CH". */
  GSW = 'gsw',

  /** Swiss German | Alemannic | Alsatian in France in Latin script. */
  GSW_FR = 'gsw-FR',

  /** Swiss German | Alemannic | Alsatian in Liechtenstein in Latin script. */
  GSW_LI = 'gsw-LI',

  /** Gujarati in India in Gujarati script. Equivalent HBO CLDR Locale: "gu-IN". */
  GU = 'gu',

  /** Gusii | Ekegusii in Kenya in Latin script. Equivalent HBO CLDR Locale: "guz-KE". */
  GUZ = 'guz',

  /** Manx in Isle of Man in Latin script. Equivalent HBO CLDR Locale: "gv-IM". */
  GV = 'gv',

  /** Hausa in Nigeria in Latin script. Equivalent HBO CLDR Locale: "ha-NG". */
  HA = 'ha',

  /** Hausa in Ghana in Latin script. */
  HA_GH = 'ha-GH',

  /** Hausa in Niger in Latin script. */
  HA_NE = 'ha-NE',

  /** Hawaiian in United States in Latin script. Equivalent HBO CLDR Locale: "haw-US". */
  HAW = 'haw',

  /** Hebrew in Israel in Hebrew script. Equivalent HBO CLDR Locale: "he-IL". */
  HE = 'he',

  /** Hindi in India in Devanagari | Nagari script. Equivalent HBO CLDR Locale: "hi-IN". */
  HI = 'hi',

  /** Croatian in Croatia in Latin script. Equivalent HBO CLDR Locale: "hr-HR". */
  HR = 'hr',

  /** Croatian in Bosnia and Herzegovina in Latin script. */
  HR_BA = 'hr-BA',

  /** Upper Sorbian in Germany in Latin script. Equivalent HBO CLDR Locale: "hsb-DE". */
  HSB = 'hsb',

  /** Hungarian in Hungary in Latin script. Equivalent HBO CLDR Locale: "hu-HU". */
  HU = 'hu',

  /** Armenian in Armenia in Armenian script. Equivalent HBO CLDR Locale: "hy-AM". */
  HY = 'hy',

  /** Interlingua (International Auxiliary Language Association) in World in Latin script. Equivalent HBO CLDR Locale: "ia-001". */
  IA = 'ia',

  /** Indonesian in Indonesia in Latin script. Equivalent HBO CLDR Locale: "id-ID". */
  ID = 'id',

  /** Igbo in Nigeria in Latin script. Equivalent HBO CLDR Locale: "ig-NG". */
  IG = 'ig',

  /** Sichuan Yi |  Nuosu in China in Yi script. Equivalent HBO CLDR Locale: "ii-CN". */
  II = 'ii',

  /** Icelandic in Iceland in Latin script. Equivalent HBO CLDR Locale: "is-IS". */
  IS = 'is',

  /** Italian in Italy in Latin script. Equivalent HBO CLDR Locale: "it-IT". */
  IT = 'it',

  /** Italian in Switzerland in Latin script. */
  IT_CH = 'it-CH',

  /** Italian in San Marino in Latin script. */
  IT_SM = 'it-SM',

  /** Italian in Holy See (Vatican City State) in Latin script. */
  IT_VA = 'it-VA',

  /** Japanese in Japan in Japanese (alias for Han + Hiragana + Katakana) script. Equivalent HBO CLDR Locale: "ja-JP". */
  JA = 'ja',

  /** Ngomba in Cameroon in Latin script. Equivalent HBO CLDR Locale: "jgo-CM". */
  JGO = 'jgo',

  /** Machame in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "jmc-TZ". */
  JMC = 'jmc',

  /** Javanese in Indonesia in Latin script. Equivalent HBO CLDR Locale: "jv-ID". */
  JV = 'jv',

  /** Georgian in Georgia in Georgian (Mkhedruli and Mtavruli) script. Equivalent HBO CLDR Locale: "ka-GE". */
  KA = 'ka',

  /** Kabyle in Algeria in Latin script. Equivalent HBO CLDR Locale: "kab-DZ". */
  KAB = 'kab',

  /** Kamba (Kenya) in Kenya in Latin script. Equivalent HBO CLDR Locale: "kam-KE". */
  KAM = 'kam',

  /** Makonde in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "kde-TZ". */
  KDE = 'kde',

  /** Kabuverdianu in Cabo Verde | Cape Verde in Latin script. Equivalent HBO CLDR Locale: "kea-CV". */
  KEA = 'kea',

  /** Koyra Chiini Songhay in Mali in Latin script. Equivalent HBO CLDR Locale: "khq-ML". */
  KHQ = 'khq',

  /** Kikuyu |  Gikuyu in Kenya in Latin script. Equivalent HBO CLDR Locale: "ki-KE". */
  KI = 'ki',

  /** Kazakh in Kazakhstan in Cyrillic script. Equivalent HBO CLDR Locale: "kk-KZ". */
  KK = 'kk',

  /** Kako in Cameroon in Latin script. Equivalent HBO CLDR Locale: "kkj-CM". */
  KKJ = 'kkj',

  /** Kalaallisut |  Greenlandic in Greenland in Latin script. Equivalent HBO CLDR Locale: "kl-GL". */
  KL = 'kl',

  /** Kalenjin in Kenya in Latin script. Equivalent HBO CLDR Locale: "kln-KE". */
  KLN = 'kln',

  /** Central Khmer in Cambodia in Khmer script. Equivalent HBO CLDR Locale: "km-KH". */
  KM = 'km',

  /** Kannada in India in Kannada script. Equivalent HBO CLDR Locale: "kn-IN". */
  KN = 'kn',

  /** Korean in Republic of Korea in Korean (alias for Hangul + Han) script. Equivalent HBO CLDR Locale: "ko-KR". */
  KO = 'ko',

  /** Korean in Democratic People's Republic of Korea in Korean (alias for Hangul + Han) script. */
  KO_KP = 'ko-KP',

  /** Konkani (macrolanguage) in India in Devanagari | Nagari script. Equivalent HBO CLDR Locale: "kok-IN". */
  KOK = 'kok',

  /** Kashmiri in India in Arabic script. Equivalent HBO CLDR Locale: "ks-IN". */
  KS = 'ks',

  /** Shambala in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "ksb-TZ". */
  KSB = 'ksb',

  /** Bafia in Cameroon in Latin script. Equivalent HBO CLDR Locale: "ksf-CM". */
  KSF = 'ksf',

  /** Kölsch in Germany in Latin script. Equivalent HBO CLDR Locale: "ksh-DE". */
  KSH = 'ksh',

  /** Kurdish in Turkey in Latin script. Equivalent HBO CLDR Locale: "ku-TR". */
  KU = 'ku',

  /** Cornish in United Kingdom in Latin script. Equivalent HBO CLDR Locale: "kw-GB". */
  KW = 'kw',

  /** Kirghiz |  Kyrgyz in Kyrgyzstan in Cyrillic script. Equivalent HBO CLDR Locale: "ky-KG". */
  KY = 'ky',

  /** Langi in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "lag-TZ". */
  LAG = 'lag',

  /** Luxembourgish |  Letzeburgesch in Luxembourg in Latin script. Equivalent HBO CLDR Locale: "lb-LU". */
  LB = 'lb',

  /** Ganda in Uganda in Latin script. Equivalent HBO CLDR Locale: "lg-UG". */
  LG = 'lg',

  /** Lakota in United States in Latin script. Equivalent HBO CLDR Locale: "lkt-US". */
  LKT = 'lkt',

  /** Lingala in The Democratic Republic of the Congo in Latin script. Equivalent HBO CLDR Locale: "ln-CD". */
  LN = 'ln',

  /** Lingala in Angola in Latin script. */
  LN_AO = 'ln-AO',

  /** Lingala in Central African Republic in Latin script. */
  LN_CF = 'ln-CF',

  /** Lingala in Congo in Latin script. */
  LN_CG = 'ln-CG',

  /** Lao in Lao People's Democratic Republic in Lao script. Equivalent HBO CLDR Locale: "lo-LA". */
  LO = 'lo',

  /** Northern Luri in Islamic Republic of Iran in Arabic script. Equivalent HBO CLDR Locale: "lrc-IR". */
  LRC = 'lrc',

  /** Northern Luri in Iraq in Arabic script. */
  LRC_IQ = 'lrc-IQ',

  /** Lithuanian in Lithuania in Latin script. Equivalent HBO CLDR Locale: "lt-LT". */
  LT = 'lt',

  /** Luba-Katanga in The Democratic Republic of the Congo in Latin script. Equivalent HBO CLDR Locale: "lu-CD". */
  LU = 'lu',

  /** Luo (Kenya and Tanzania) | Dholuo in Kenya in Latin script. Equivalent HBO CLDR Locale: "luo-KE". */
  LUO = 'luo',

  /** Luyia | Oluluyia in Kenya in Latin script. Equivalent HBO CLDR Locale: "luy-KE". */
  LUY = 'luy',

  /** Latvian in Latvia in Latin script. Equivalent HBO CLDR Locale: "lv-LV". */
  LV = 'lv',

  /** Masai in Kenya in Latin script. Equivalent HBO CLDR Locale: "mas-KE". */
  MAS = 'mas',

  /** Masai in United Republic of Tanzania in Latin script. */
  MAS_TZ = 'mas-TZ',

  /** Meru in Kenya in Latin script. Equivalent HBO CLDR Locale: "mer-KE". */
  MER = 'mer',

  /** Morisyen in Mauritius in Latin script. Equivalent HBO CLDR Locale: "mfe-MU". */
  MFE = 'mfe',

  /** Malagasy in Madagascar in Latin script. Equivalent HBO CLDR Locale: "mg-MG". */
  MG = 'mg',

  /** Makhuwa-Meetto in Mozambique in Latin script. Equivalent HBO CLDR Locale: "mgh-MZ". */
  MGH = 'mgh',

  /** Meta' in Cameroon in Latin script. Equivalent HBO CLDR Locale: "mgo-CM". */
  MGO = 'mgo',

  /** Maori in New Zealand in Latin script. Equivalent HBO CLDR Locale: "mi-NZ". */
  MI = 'mi',

  /** Macedonian in North Macedonia in Cyrillic script. Equivalent HBO CLDR Locale: "mk-MK". */
  MK = 'mk',

  /** Malayalam in India in Malayalam script. Equivalent HBO CLDR Locale: "ml-IN". */
  ML = 'ml',

  /** Mongolian in Mongolia in Cyrillic script. Equivalent HBO CLDR Locale: "mn-MN". */
  MN = 'mn',

  /** Marathi in India in Devanagari | Nagari script. Equivalent HBO CLDR Locale: "mr-IN". */
  MR = 'mr',

  /** Malay in Malaysia in Latin script. Equivalent HBO CLDR Locale: "ms-MY". */
  MS = 'ms',

  /** Malay in Brunei Darussalam in Latin script. */
  MS_BN = 'ms-BN',

  /** Malay in Singapore in Latin script. */
  MS_SG = 'ms-SG',

  /** Maltese in Malta in Latin script. Equivalent HBO CLDR Locale: "mt-MT". */
  MT = 'mt',

  /** Mundang in Cameroon in Latin script. Equivalent HBO CLDR Locale: "mua-CM". */
  MUA = 'mua',

  /** Burmese in Myanmar in Myanmar | Burmese script. Equivalent HBO CLDR Locale: "my-MM". */
  MY = 'my',

  /** Mazanderani in Islamic Republic of Iran in Arabic script. Equivalent HBO CLDR Locale: "mzn-IR". */
  MZN = 'mzn',

  /** Khoekhoe | Nama (Namibia) in Namibia in Latin script. Equivalent HBO CLDR Locale: "naq-NA". */
  NAQ = 'naq',

  /** Bokmål, Norwegian |  Norwegian Bokmål in Norway in Latin script. Equivalent HBO CLDR Locale: "nb-NO". */
  NB = 'nb',

  /** Bokmål, Norwegian |  Norwegian Bokmål in Svalbard and Jan Mayen in Latin script. */
  NB_SJ = 'nb-SJ',

  /** Ndebele, North |  North Ndebele in Zimbabwe in Latin script. Equivalent HBO CLDR Locale: "nd-ZW". */
  ND = 'nd',

  /** Low German | Low Saxon in Germany in Latin script. Equivalent HBO CLDR Locale: "nds-DE". */
  NDS = 'nds',

  /** Low German | Low Saxon in Netherlands in Latin script. */
  NDS_NL = 'nds-NL',

  /** Nepali in Nepal in Devanagari | Nagari script. Equivalent HBO CLDR Locale: "ne-NP". */
  NE = 'ne',

  /** Nepali in India in Devanagari | Nagari script. */
  NE_IN = 'ne-IN',

  /** Dutch |  Flemish in Netherlands in Latin script. Equivalent HBO CLDR Locale: "nl-NL". */
  NL = 'nl',

  /** Dutch |  Flemish in Aruba in Latin script. */
  NL_AW = 'nl-AW',

  /** Dutch |  Flemish in Belgium in Latin script. */
  NL_BE = 'nl-BE',

  /** Dutch |  Flemish in Bonaire, Sint Eustatius and Saba in Latin script. */
  NL_BQ = 'nl-BQ',

  /** Dutch |  Flemish in Curaçao in Latin script. */
  NL_CW = 'nl-CW',

  /** Dutch |  Flemish in Suriname in Latin script. */
  NL_SR = 'nl-SR',

  /** Dutch |  Flemish in Sint Maarten (Dutch part) in Latin script. */
  NL_SX = 'nl-SX',

  /** Kwasio in Cameroon in Latin script. Equivalent HBO CLDR Locale: "nmg-CM". */
  NMG = 'nmg',

  /** Norwegian Nynorsk |  Nynorsk, Norwegian in Norway in Latin script. Equivalent HBO CLDR Locale: "nn-NO". */
  NN = 'nn',

  /** Ngiemboon in Cameroon in Latin script. Equivalent HBO CLDR Locale: "nnh-CM". */
  NNH = 'nnh',

  /** Nuer in South Sudan in Latin script. Equivalent HBO CLDR Locale: "nus-SS". */
  NUS = 'nus',

  /** Nyankole in Uganda in Latin script. Equivalent HBO CLDR Locale: "nyn-UG". */
  NYN = 'nyn',

  /** Oromo in Ethiopia in Latin script. Equivalent HBO CLDR Locale: "om-ET". */
  OM = 'om',

  /** Oromo in Kenya in Latin script. */
  OM_KE = 'om-KE',

  /** Oriya in India in Oriya | Odia script. Equivalent HBO CLDR Locale: "or-IN". */
  OR = 'or',

  /** Ossetian |  Ossetic in Georgia in Cyrillic script. Equivalent HBO CLDR Locale: "os-GE". */
  OS = 'os',

  /** Ossetian |  Ossetic in Russian Federation in Cyrillic script. */
  OS_RU = 'os-RU',

  /** Panjabi |  Punjabi in Pakistan in Arabic script. Equivalent HBO CLDR Locale: "pa-PK". */
  PA_ARAB = 'pa-Arab',

  /** Panjabi |  Punjabi in India in Gurmukhi script. Equivalent HBO CLDR Locale: "pa-IN". */
  PA_GURU = 'pa-Guru',

  /** Polish in Poland in Latin script. Equivalent HBO CLDR Locale: "pl-PL". */
  PL = 'pl',

  /** Prussian in World in Latin script. Equivalent HBO CLDR Locale: "prg-001". */
  PRG = 'prg',

  /** Pushto |  Pashto in Afghanistan in Arabic script. Equivalent HBO CLDR Locale: "ps-AF". */
  PS = 'ps',

  /** Pushto |  Pashto in Pakistan in Arabic script. */
  PS_PK = 'ps-PK',

  /** Portuguese in Brazil in Latin script. Equivalent HBO CLDR Locale: "pt-BR". */
  PT = 'pt',

  /** Portuguese in Angola in Latin script. */
  PT_AO = 'pt-AO',

  /** Portuguese in Switzerland in Latin script. */
  PT_CH = 'pt-CH',

  /** Portuguese in Cabo Verde | Cape Verde in Latin script. */
  PT_CV = 'pt-CV',

  /** Portuguese in Equatorial Guinea in Latin script. */
  PT_GQ = 'pt-GQ',

  /** Portuguese in Guinea-Bissau in Latin script. */
  PT_GW = 'pt-GW',

  /** Portuguese in Luxembourg in Latin script. */
  PT_LU = 'pt-LU',

  /** Portuguese in Macao in Latin script. */
  PT_MO = 'pt-MO',

  /** Portuguese in Mozambique in Latin script. */
  PT_MZ = 'pt-MZ',

  /** Portuguese in Portugal in Latin script. */
  PT_PT = 'pt-PT',

  /** Portuguese in Sao Tome and Principe in Latin script. */
  PT_ST = 'pt-ST',

  /** Portuguese in Timor-Leste in Latin script. */
  PT_TL = 'pt-TL',

  /** Quechua in Peru in Latin script. Equivalent HBO CLDR Locale: "qu-PE". */
  QU = 'qu',

  /** Quechua in Bolivia in Latin script. */
  QU_BO = 'qu-BO',

  /** Quechua in Ecuador in Latin script. */
  QU_EC = 'qu-EC',

  /** Romansh in Switzerland in Latin script. Equivalent HBO CLDR Locale: "rm-CH". */
  RM = 'rm',

  /** Rundi in Burundi in Latin script. Equivalent HBO CLDR Locale: "rn-BI". */
  RN = 'rn',

  /** Romanian |  Moldavian |  Moldovan in Romania in Latin script. Equivalent HBO CLDR Locale: "ro-RO". */
  RO = 'ro',

  /** Romanian |  Moldavian |  Moldovan in Moldova in Latin script. */
  RO_MD = 'ro-MD',

  /** Rombo in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "rof-TZ". */
  ROF = 'rof',

  /** Russian in Russian Federation in Cyrillic script. Equivalent HBO CLDR Locale: "ru-RU". */
  RU = 'ru',

  /** Russian in Belarus in Cyrillic script. */
  RU_BY = 'ru-BY',

  /** Russian in Kyrgyzstan in Cyrillic script. */
  RU_KG = 'ru-KG',

  /** Russian in Kazakhstan in Cyrillic script. */
  RU_KZ = 'ru-KZ',

  /** Russian in Moldova in Cyrillic script. */
  RU_MD = 'ru-MD',

  /** Russian in Ukraine in Cyrillic script. */
  RU_UA = 'ru-UA',

  /** Kinyarwanda in Rwanda in Latin script. Equivalent HBO CLDR Locale: "rw-RW". */
  RW = 'rw',

  /** Rwa in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "rwk-TZ". */
  RWK = 'rwk',

  /** Yakut in Russian Federation in Cyrillic script. Equivalent HBO CLDR Locale: "sah-RU". */
  SAH = 'sah',

  /** Samburu in Kenya in Latin script. Equivalent HBO CLDR Locale: "saq-KE". */
  SAQ = 'saq',

  /** Sangu (Tanzania) in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "sbp-TZ". */
  SBP = 'sbp',

  /** Sindhi in Pakistan in Arabic script. Equivalent HBO CLDR Locale: "sd-PK". */
  SD = 'sd',

  /** Northern Sami in Norway in Latin script. Equivalent HBO CLDR Locale: "se-NO". */
  SE = 'se',

  /** Northern Sami in Finland in Latin script. */
  SE_FI = 'se-FI',

  /** Northern Sami in Sweden in Latin script. */
  SE_SE = 'se-SE',

  /** Sena in Mozambique in Latin script. Equivalent HBO CLDR Locale: "seh-MZ". */
  SEH = 'seh',

  /** Koyraboro Senni Songhai in Mali in Latin script. Equivalent HBO CLDR Locale: "ses-ML". */
  SES = 'ses',

  /** Sango in Central African Republic in Latin script. Equivalent HBO CLDR Locale: "sg-CF". */
  SG = 'sg',

  /** Tachelhit in Morocco in Tifinagh | Berber script. Equivalent HBO CLDR Locale: "shi-MA". */
  SHI_TFNG = 'shi-Tfng',

  /** Sinhala |  Sinhalese in Sri Lanka in Sinhala script. Equivalent HBO CLDR Locale: "si-LK". */
  SI = 'si',

  /** Slovak in Slovakia in Latin script. Equivalent HBO CLDR Locale: "sk-SK". */
  SK = 'sk',

  /** Slovenian in Slovenia in Latin script. Equivalent HBO CLDR Locale: "sl-SI". */
  SL = 'sl',

  /** Inari Sami in Finland in Latin script. Equivalent HBO CLDR Locale: "smn-FI". */
  SMN = 'smn',

  /** Shona in Zimbabwe in Latin script. Equivalent HBO CLDR Locale: "sn-ZW". */
  SN = 'sn',

  /** Somali in Somalia in Latin script. Equivalent HBO CLDR Locale: "so-SO". */
  SO = 'so',

  /** Somali in Djibouti in Latin script. */
  SO_DJ = 'so-DJ',

  /** Somali in Ethiopia in Latin script. */
  SO_ET = 'so-ET',

  /** Somali in Kenya in Latin script. */
  SO_KE = 'so-KE',

  /** Albanian in Albania in Latin script. Equivalent HBO CLDR Locale: "sq-AL". */
  SQ = 'sq',

  /** Albanian in North Macedonia in Latin script. */
  SQ_MK = 'sq-MK',

  /** Albanian in Kosovo in Latin script. */
  SQ_XK = 'sq-XK',

  /** Serbian in Serbia in Latin script. Equivalent HBO CLDR Locale: "sr-Latn-RS". */
  SR_LATN = 'sr-Latn',

  /** Serbian in Bosnia and Herzegovina in Latin script. */
  SR_LATN_BA = 'sr-Latn-BA',

  /** Serbian in Montenegro in Latin script. */
  SR_LATN_ME = 'sr-Latn-ME',

  /** Serbian in Kosovo in Latin script. */
  SR_LATN_XK = 'sr-Latn-XK',

  /** Swedish in Sweden in Latin script. Equivalent HBO CLDR Locale: "sv-SE". */
  SV = 'sv',

  /** Swedish in Åland Islands in Latin script. */
  SV_AX = 'sv-AX',

  /** Swedish in Finland in Latin script. */
  SV_FI = 'sv-FI',

  /** Swahili in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "sw-TZ". */
  SW = 'sw',

  /** Swahili in The Democratic Republic of the Congo in Latin script. */
  SW_CD = 'sw-CD',

  /** Swahili in Kenya in Latin script. */
  SW_KE = 'sw-KE',

  /** Swahili in Uganda in Latin script. */
  SW_UG = 'sw-UG',

  /** Tamil in India in Tamil script. Equivalent HBO CLDR Locale: "ta-IN". */
  TA = 'ta',

  /** Tamil in Sri Lanka in Tamil script. */
  TA_LK = 'ta-LK',

  /** Tamil in Malaysia in Tamil script. */
  TA_MY = 'ta-MY',

  /** Tamil in Singapore in Tamil script. */
  TA_SG = 'ta-SG',

  /** Telugu in India in Telugu script. Equivalent HBO CLDR Locale: "te-IN". */
  TE = 'te',

  /** Teso in Uganda in Latin script. Equivalent HBO CLDR Locale: "teo-UG". */
  TEO = 'teo',

  /** Teso in Kenya in Latin script. */
  TEO_KE = 'teo-KE',

  /** Tajik in Tajikistan in Cyrillic script. Equivalent HBO CLDR Locale: "tg-TJ". */
  TG = 'tg',

  /** Thai in Thailand in Thai script. Equivalent HBO CLDR Locale: "th-TH". */
  TH = 'th',

  /** Tigrinya in Ethiopia in Ethiopic | Geʻez | Ge'ez script. Equivalent HBO CLDR Locale: "ti-ET". */
  TI = 'ti',

  /** Tigrinya in Eritrea in Ethiopic | Geʻez | Ge'ez script. */
  TI_ER = 'ti-ER',

  /** Turkmen in Turkmenistan in Latin script. Equivalent HBO CLDR Locale: "tk-TM". */
  TK = 'tk',

  /** Tonga (Tonga Islands) in Tonga in Latin script. Equivalent HBO CLDR Locale: "to-TO". */
  TO = 'to',

  /** Turkish in Turkey in Latin script. Equivalent HBO CLDR Locale: "tr-TR". */
  TR = 'tr',

  /** Turkish in Cyprus in Latin script. */
  TR_CY = 'tr-CY',

  /** Tatar in Russian Federation in Cyrillic script. Equivalent HBO CLDR Locale: "tt-RU". */
  TT = 'tt',

  /** Tasawaq in Niger in Latin script. Equivalent HBO CLDR Locale: "twq-NE". */
  TWQ = 'twq',

  /** Central Atlas Tamazight in Morocco in Latin script. Equivalent HBO CLDR Locale: "tzm-MA". */
  TZM = 'tzm',

  /** Uighur |  Uyghur in China in Arabic script. Equivalent HBO CLDR Locale: "ug-CN". */
  UG = 'ug',

  /** Ukrainian in Ukraine in Cyrillic script. Equivalent HBO CLDR Locale: "uk-UA". */
  UK = 'uk',

  /** Urdu in Pakistan in Arabic script. Equivalent HBO CLDR Locale: "ur-PK". */
  UR = 'ur',

  /** Urdu in India in Arabic script. */
  UR_IN = 'ur-IN',

  /** Uzbek in Afghanistan in Arabic script. Equivalent HBO CLDR Locale: "uz-AF". */
  UZ_ARAB = 'uz-Arab',

  /** Uzbek in Uzbekistan in Latin script. Equivalent HBO CLDR Locale: "uz-UZ". */
  UZ_LATN = 'uz-Latn',

  /** Vai in Liberia in Vai script. Equivalent HBO CLDR Locale: "vai-LR". */
  VAI_VAII = 'vai-Vaii',

  /** Vietnamese in Viet Nam in Latin script. Equivalent HBO CLDR Locale: "vi-VN". */
  VI = 'vi',

  /** Volapük in World in Latin script. Equivalent HBO CLDR Locale: "vo-001". */
  VO = 'vo',

  /** Vunjo in United Republic of Tanzania in Latin script. Equivalent HBO CLDR Locale: "vun-TZ". */
  VUN = 'vun',

  /** Walser in Switzerland in Latin script. Equivalent HBO CLDR Locale: "wae-CH". */
  WAE = 'wae',

  /** Wolof in Senegal in Latin script. Equivalent HBO CLDR Locale: "wo-SN". */
  WO = 'wo',

  /** Xhosa in South Africa in Latin script. Equivalent HBO CLDR Locale: "xh-ZA". */
  XH = 'xh',

  /** Soga in Uganda in Latin script. Equivalent HBO CLDR Locale: "xog-UG". */
  XOG = 'xog',

  /** Yangben in Cameroon in Latin script. Equivalent HBO CLDR Locale: "yav-CM". */
  YAV = 'yav',

  /** Yiddish in World in Hebrew script. Equivalent HBO CLDR Locale: "yi-001". */
  YI = 'yi',

  /** Yoruba in Nigeria in Latin script. Equivalent HBO CLDR Locale: "yo-NG". */
  YO = 'yo',

  /** Yoruba in Benin in Latin script. */
  YO_BJ = 'yo-BJ',

  /** Yue Chinese | Cantonese in China in Han (Simplified variant) script. Equivalent HBO CLDR Locale: "yue-CN". */
  YUE_HANS = 'yue-Hans',

  /** Yue Chinese | Cantonese in Hong Kong in Han (Traditional variant) script. Equivalent HBO CLDR Locale: "yue-HK". */
  YUE_HANT = 'yue-Hant',

  /** Standard Moroccan Tamazight in Morocco in Tifinagh | Berber script. Equivalent HBO CLDR Locale: "zgh-MA". */
  ZGH = 'zgh',

  /** Chinese in China in Han (Simplified variant) script. Equivalent HBO CLDR Locale: "zh-CN". */
  ZH_HANS = 'zh-Hans',

  /** Chinese in Hong Kong in Han (Simplified variant) script. */
  ZH_HANS_HK = 'zh-Hans-HK',

  /** Chinese in Macao in Han (Simplified variant) script. */
  ZH_HANS_MO = 'zh-Hans-MO',

  /** Chinese in Singapore in Han (Simplified variant) script. */
  ZH_HANS_SG = 'zh-Hans-SG',

  /** Chinese in Taiwan, Province of China in Han (Traditional variant) script. Equivalent HBO CLDR Locale: "zh-TW". */
  ZH_HANT = 'zh-Hant',

  /** Chinese in Hong Kong in Han (Traditional variant) script. */
  ZH_HANT_HK = 'zh-Hant-HK',

  /** Chinese in Macao in Han (Traditional variant) script. */
  ZH_HANT_MO = 'zh-Hant-MO',

  /** Zulu in South Africa in Latin script. Equivalent HBO CLDR Locale: "zu-ZA". */
  ZU = 'zu'
}

// prettier-ignore
/**
 * set of values from the CldrLocale const enums
 * @public
 */
export const CldrLocaleSet: ReadonlySet<CldrLocale> = new Set([
'af', 'af-NA', 'agq', 'ak', 'am', 'ar-AE', 'ar-BH', 'ar-DJ', 'ar-DZ', 'ar-EG', 'ar-EH', 'ar-ER', 'ar-IL', 'ar-IQ', 'ar-JO', 'ar-KM', 'ar-KW', 'ar-LB', 'ar-LY', 'ar-MA', 'ar-MR', 'ar-OM', 'ar-PS', 'ar-QA', 'ar-SA', 'ar-SD', 'ar-SO', 'ar-SS', 'ar-SY', 'ar-TD', 'ar-TN', 'ar-YE', 'as', 'asa', 'ast', 'az-Latn', 'bas', 'be', 'bem', 'bez', 'bg', 'bm', 'bn', 'bn-IN', 'bo', 'bo-IN', 'br', 'brx', 'bs-Latn', 'ca', 'ca-AD', 'ca-FR', 'ca-IT', 'ccp', 'ccp-IN', 'ce', 'ceb', 'cgg', 'chr', 'ckb', 'ckb-IR', 'cs', 'cu', 'cy', 'da', 'da-GL', 'dav', 'de', 'de-AT', 'de-BE', 'de-CH', 'de-IT', 'de-LI', 'de-LU', 'dje', 'dsb', 'dua', 'dyo', 'dz', 'ebu', 'ee', 'ee-TG', 'el', 'el-CY', 'en', 'en-001', 'en-150', 'en-AE', 'en-AG', 'en-AI', 'en-AS', 'en-AT', 'en-AU', 'en-BB', 'en-BE', 'en-BI', 'en-BM', 'en-BS', 'en-BW', 'en-BZ', 'en-CA', 'en-CC', 'en-CH', 'en-CK', 'en-CM', 'en-CX', 'en-CY', 'en-DE', 'en-DG', 'en-DK', 'en-DM', 'en-ER', 'en-FI', 'en-FJ', 'en-FK', 'en-FM', 'en-GB', 'en-GD', 'en-GG', 'en-GH', 'en-GI', 'en-GM', 'en-GU', 'en-GY', 'en-HK', 'en-IE', 'en-IL', 'en-IM', 'en-IN', 'en-IO', 'en-JE', 'en-JM', 'en-KE', 'en-KI', 'en-KN', 'en-KY', 'en-LC', 'en-LR', 'en-LS', 'en-MG', 'en-MH', 'en-MO', 'en-MP', 'en-MS', 'en-MT', 'en-MU', 'en-MW', 'en-MY', 'en-NA', 'en-NF', 'en-NG', 'en-NL', 'en-NR', 'en-NU', 'en-NZ', 'en-PG', 'en-PH', 'en-PK', 'en-PN', 'en-PR', 'en-PW', 'en-RW', 'en-SB', 'en-SC', 'en-SD', 'en-SE', 'en-SG', 'en-SH', 'en-SI', 'en-SL', 'en-SS', 'en-SX', 'en-SZ', 'en-TC', 'en-TK', 'en-TO', 'en-TT', 'en-TV', 'en-TZ', 'en-UG', 'en-UM', 'en-VC', 'en-VG', 'en-VI', 'en-VU', 'en-WS', 'en-ZA', 'en-ZM', 'en-ZW', 'eo', 'es', 'es-419', 'es-AR', 'es-BO', 'es-BR', 'es-BZ', 'es-CL', 'es-CO', 'es-CR', 'es-CU', 'es-DO', 'es-EA', 'es-EC', 'es-GQ', 'es-GT', 'es-HN', 'es-IC', 'es-MX', 'es-NI', 'es-PA', 'es-PE', 'es-PH', 'es-PR', 'es-PY', 'es-SV', 'es-US', 'es-UY', 'es-VE', 'et', 'eu', 'ewo', 'fa', 'fa-AF', 'ff-Latn', 'ff-Latn-BF', 'ff-Latn-CM', 'ff-Latn-GH', 'ff-Latn-GM', 'ff-Latn-GN', 'ff-Latn-GW', 'ff-Latn-LR', 'ff-Latn-MR', 'ff-Latn-NE', 'ff-Latn-NG', 'ff-Latn-SL', 'fi', 'fil', 'fo', 'fo-DK', 'fr', 'fr-BE', 'fr-BF', 'fr-BI', 'fr-BJ', 'fr-BL', 'fr-CA', 'fr-CD', 'fr-CF', 'fr-CG', 'fr-CH', 'fr-CI', 'fr-CM', 'fr-DJ', 'fr-DZ', 'fr-GA', 'fr-GF', 'fr-GN', 'fr-GP', 'fr-GQ', 'fr-HT', 'fr-KM', 'fr-LU', 'fr-MA', 'fr-MC', 'fr-MF', 'fr-MG', 'fr-ML', 'fr-MQ', 'fr-MR', 'fr-MU', 'fr-NC', 'fr-NE', 'fr-PF', 'fr-PM', 'fr-RE', 'fr-RW', 'fr-SC', 'fr-SN', 'fr-SY', 'fr-TD', 'fr-TG', 'fr-TN', 'fr-VU', 'fr-WF', 'fr-YT', 'fur', 'fy', 'ga', 'ga-GB', 'gd', 'gl', 'gsw', 'gsw-FR', 'gsw-LI', 'gu', 'guz', 'gv', 'ha', 'ha-GH', 'ha-NE', 'haw', 'he', 'hi', 'hr', 'hr-BA', 'hsb', 'hu', 'hy', 'ia', 'id', 'ig', 'ii', 'is', 'it', 'it-CH', 'it-SM', 'it-VA', 'ja', 'jgo', 'jmc', 'jv', 'ka', 'kab', 'kam', 'kde', 'kea', 'khq', 'ki', 'kk', 'kkj', 'kl', 'kln', 'km', 'kn', 'ko', 'ko-KP', 'kok', 'ks', 'ksb', 'ksf', 'ksh', 'ku', 'kw', 'ky', 'lag', 'lb', 'lg', 'lkt', 'ln', 'ln-AO', 'ln-CF', 'ln-CG', 'lo', 'lrc', 'lrc-IQ', 'lt', 'lu', 'luo', 'luy', 'lv', 'mas', 'mas-TZ', 'mer', 'mfe', 'mg', 'mgh', 'mgo', 'mi', 'mk', 'ml', 'mn', 'mr', 'ms', 'ms-BN', 'ms-SG', 'mt', 'mua', 'my', 'mzn', 'naq', 'nb', 'nb-SJ', 'nd', 'nds', 'nds-NL', 'ne', 'ne-IN', 'nl', 'nl-AW', 'nl-BE', 'nl-BQ', 'nl-CW', 'nl-SR', 'nl-SX', 'nmg', 'nn', 'nnh', 'nus', 'nyn', 'om', 'om-KE', 'or', 'os', 'os-RU', 'pa-Arab', 'pa-Guru', 'pl', 'prg', 'ps', 'ps-PK', 'pt', 'pt-AO', 'pt-CH', 'pt-CV', 'pt-GQ', 'pt-GW', 'pt-LU', 'pt-MO', 'pt-MZ', 'pt-PT', 'pt-ST', 'pt-TL', 'qu', 'qu-BO', 'qu-EC', 'rm', 'rn', 'ro', 'ro-MD', 'rof', 'ru', 'ru-BY', 'ru-KG', 'ru-KZ', 'ru-MD', 'ru-UA', 'rw', 'rwk', 'sah', 'saq', 'sbp', 'sd', 'se', 'se-FI', 'se-SE', 'seh', 'ses', 'sg', 'shi-Tfng', 'si', 'sk', 'sl', 'smn', 'sn', 'so', 'so-DJ', 'so-ET', 'so-KE', 'sq', 'sq-MK', 'sq-XK', 'sr-Latn', 'sr-Latn-BA', 'sr-Latn-ME', 'sr-Latn-XK', 'sv', 'sv-AX', 'sv-FI', 'sw', 'sw-CD', 'sw-KE', 'sw-UG', 'ta', 'ta-LK', 'ta-MY', 'ta-SG', 'te', 'teo', 'teo-KE', 'tg', 'th', 'ti', 'ti-ER', 'tk', 'to', 'tr', 'tr-CY', 'tt', 'twq', 'tzm', 'ug', 'uk', 'ur', 'ur-IN', 'uz-Arab', 'uz-Latn', 'vai-Vaii', 'vi', 'vo', 'vun', 'wae', 'wo', 'xh', 'xog', 'yav', 'yi', 'yo', 'yo-BJ', 'yue-Hans', 'yue-Hant', 'zgh', 'zh-Hans', 'zh-Hans-HK', 'zh-Hans-MO', 'zh-Hans-SG', 'zh-Hant', 'zh-Hant-HK', 'zh-Hant-MO', 'zu'
]) as Set<CldrLocale>;
